/**
 * Vue Mixin for Navigation Drawer and Country Selector Accessibility
 *
 * This mixin provides keyboard navigation and focus management for:
 * - Navigation drawer (hamburger menu)
 * - Country selector overlay
 * - Focus trapping and proper ARIA handling
 */

export const NavigationAccessibilityMixin = {
    mounted() {
        // Add global escape key handler
        document.addEventListener('keydown', this.handleGlobalEscape);
    },

    beforeDestroy() {
        // Clean up global escape key handler
        document.removeEventListener('keydown', this.handleGlobalEscape);
    },

    methods: {
        focusWithoutScroll(element) {
            try {
                element.focus({ preventScroll: true });
            } catch (error) {
                // Fallback for browsers that do not support focus options.
                const scrollX = window.scrollX;
                const scrollY = window.scrollY;
                element.focus();
                window.scrollTo(scrollX, scrollY);
            }
        },

        // Global escape key handler
        handleGlobalEscape(event) {
            if (event.key === 'Escape') {
                // Close only the last opened overlay
                if (this.showCountries) {
                    // If country selector is open, close it first
                    this.showCountries = false;

                    // Focus back to the country selector trigger button
                    this.$nextTick(() => {
                        // Find the country selector button that's currently visible
                        const countryButton = this.showMenu
                            ? document.querySelector(
                                  '.drawer__country-content button',
                              )
                            : document.querySelector(
                                  '.header__country-selector[type="button"]',
                              );
                        if (countryButton) {
                            this.focusWithoutScroll(countryButton);
                        }
                    });
                } else if (this.showMenu) {
                    // If only menu is open, close it
                    this.showMenu = false;

                    // Focus back to hamburger button
                    this.$nextTick(() => {
                        const hamburgerButton = this.$refs.hamburgerButton;
                        if (hamburgerButton) {
                            this.focusWithoutScroll(hamburgerButton);
                        }
                    });
                }
            }
        },

        // Simplified keydown handler - only for drawer items navigation
        handleDrawerKeydown(event) {
            // Tab navigation is now handled by browser via tabindex management
            // Keep only specific navigation enhancements
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                const target = event.target;
                if (target.classList.contains('drawer__link--expandable')) {
                    event.preventDefault();
                    if (event.key === 'ArrowDown') {
                        this.focusNextDrawerItem(target);
                    } else {
                        this.focusPreviousDrawerItem(target);
                    }
                }
            }
        },

        handleDrawerItemKeydown(event, index) {
            // Handle Enter and Space keys to trigger hover/expand
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.setDrawerItemHover(index);

                // Focus the first focusable element in the expanded group
                this.$nextTick(() => {
                    const expandedGroup = document.querySelector(
                        `.drawer__group.index-${index}`,
                    );
                    if (expandedGroup) {
                        const firstFocusable = expandedGroup.querySelector(
                            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                        );
                        if (firstFocusable) {
                            firstFocusable.focus();
                        }
                    }
                });
            }

            // Handle arrow keys for navigation
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.focusNextDrawerItem(event.target);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.focusPreviousDrawerItem(event.target);
            }
        },

        handleHamburgerKeydown(event) {
            // Handle Enter and Space keys to toggle menu
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.updateMenu();

                // Focus the navigation drawer after opening
                if (this.showMenu) {
                    this.$nextTick(() => {
                        const navigationDrawer = this.$refs.navigationDrawer;
                        if (navigationDrawer) {
                            // Find the first focusable element in the drawer
                            const firstFocusable =
                                navigationDrawer.querySelector(
                                    'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
                                );
                            if (firstFocusable) {
                                firstFocusable.focus();
                            } else {
                                navigationDrawer.focus();
                            }
                        }
                    });
                }
            }
        },

        handleCountrySelectorKeydown(event) {
            // Handle Enter and Space keys to toggle country selector
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.showCountries = !this.showCountries;

                // Focus the country switcher after opening
                if (this.showCountries) {
                    this.$nextTick(() => {
                        const countrySwitcher = this.$refs.countrySwitcher;
                        if (countrySwitcher) {
                            // Find the first focusable element in the country switcher
                            const firstFocusable =
                                countrySwitcher.querySelector(
                                    'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
                                );
                            if (firstFocusable) {
                                firstFocusable.focus();
                            } else {
                                countrySwitcher.focus();
                            }
                        }
                    });
                }
            }
        },

        focusNextDrawerItem(currentElement) {
            const drawerItems = document.querySelectorAll(
                '.drawer__menu button.drawer__link--expandable',
            );
            const currentIndex =
                Array.from(drawerItems).indexOf(currentElement);
            const nextIndex = (currentIndex + 1) % drawerItems.length;
            if (drawerItems[nextIndex]) {
                drawerItems[nextIndex].focus();
            }
        },

        focusPreviousDrawerItem(currentElement) {
            const drawerItems = document.querySelectorAll(
                '.drawer__menu button.drawer__link--expandable',
            );
            const currentIndex =
                Array.from(drawerItems).indexOf(currentElement);
            const previousIndex =
                (currentIndex - 1 + drawerItems.length) % drawerItems.length;
            if (drawerItems[previousIndex]) {
                drawerItems[previousIndex].focus();
            }
        },

        addGlobalFocusTrap() {
            // Set main page wrapper to be unfocusable
            const pageWrapper = document.querySelector('main#page-wrapper');
            if (pageWrapper) {
                pageWrapper.setAttribute('tabindex', '-1');
            }

            const footer = document.querySelector('footer');
            if (footer) {
                footer.setAttribute('tabindex', '-1');
            }

            // Handle priority-based tabindex management
            if (this.showCountries) {
                // Priority 1: Country selector is open - only it and its close button should be focusable
                const countrySwitcher = this.$refs.countrySwitcher;
                const countryCloseButton =
                    document.getElementById('hamburger-v2-main');

                // Set country switcher container to be focusable (browser handles children)
                if (countrySwitcher) {
                    countrySwitcher.setAttribute('tabindex', '0');
                }

                if (countryCloseButton) {
                    countryCloseButton.setAttribute('tabindex', '0');
                }

                // Disable navigation skipToContent
                const skipToContent = this.$refs.skipToContent;
                if (skipToContent) {
                    skipToContent.setAttribute('tabindex', '-1');
                }

                // Disable hamburger button
                const hamburgerButton = this.$refs.hamburgerButton;
                if (hamburgerButton) {
                    hamburgerButton.setAttribute('tabindex', '-1');
                }
            } else if (this.showMenu) {
                // Priority 2: Only menu is open - skipToContent and hamburger button should be focusable
                const skipToContent = this.$refs.skipToContent;
                const hamburgerButton = this.$refs.hamburgerButton;

                // Set skipToContent container to be focusable (browser handles children)
                if (skipToContent) {
                    skipToContent.setAttribute('tabindex', '-1');
                }

                // Set hamburger button to be focusable
                if (hamburgerButton) {
                    hamburgerButton.setAttribute('tabindex', '0');
                }
            }

            // Disable all other page elements
            const allOtherElements = document.querySelectorAll(
                'header > *:not(.drawer__collapsable-menu):not(.countries-switcher) button, header > *:not(.drawer__collapsable-menu):not(.countries-switcher) [href], header > *:not(.drawer__collapsable-menu):not(.countries-switcher) input, header > *:not(.drawer__collapsable-menu):not(.countries-switcher) select, header > *:not(.drawer__collapsable-menu):not(.countries-switcher) textarea, main button, main [href], main input, main select, main textarea, footer button, footer [href], footer input, footer select, footer textarea',
            );

            allOtherElements.forEach((el) => {
                // Don't disable elements that are part of the active overlay
                const isInActiveOverlay =
                    (this.showCountries &&
                        this.$refs.countrySwitcher &&
                        this.$refs.countrySwitcher.contains(el)) ||
                    (this.showMenu &&
                        this.$refs.navigationDrawer &&
                        this.$refs.navigationDrawer.contains(el)) ||
                    (this.showMenu &&
                        this.$refs.hamburgerButton &&
                        el === this.$refs.hamburgerButton) ||
                    (this.showCountries &&
                        document.getElementById('hamburger-v2-main') &&
                        el === document.getElementById('hamburger-v2-main'));

                if (!isInActiveOverlay) {
                    el.setAttribute('data-focus-trap-disabled', 'true');
                    el.setAttribute('tabindex', '-1');
                }
            });
        },

        removeGlobalFocusTrap() {
            // Restore main page wrapper
            const pageWrapper = document.querySelector('main#page-wrapper');
            if (pageWrapper) {
                pageWrapper.removeAttribute('tabindex');
            }

            const footer = document.querySelector('footer');
            if (footer) {
                footer.removeAttribute('tabindex');
            }

            // Restore all disabled elements
            const disabledElements = document.querySelectorAll(
                '[data-focus-trap-disabled="true"]',
            );
            disabledElements.forEach((el) => {
                el.removeAttribute('data-focus-trap-disabled');
                el.removeAttribute('tabindex');
            });

            // Reset navigation skipToContent to default state
            const skipToContent = this.$refs.skipToContent;
            if (skipToContent) {
                skipToContent.removeAttribute('tabindex');
            }

            // Reset country switcher container to default state
            const countrySwitcher = this.$refs.countrySwitcher;
            if (countrySwitcher) {
                countrySwitcher.setAttribute('tabindex', '-1'); // Default state
            }

            // Reset hamburger button
            const hamburgerButton = this.$refs.hamburgerButton;
            if (hamburgerButton) {
                hamburgerButton.removeAttribute('tabindex');
            }

            // Reset country close button
            const countryCloseButton =
                document.getElementById('hamburger-v2-main');
            if (countryCloseButton) {
                countryCloseButton.removeAttribute('tabindex');
            }
        },
    },
    watch: {
        showMenu: function (visible) {
            if (visible) {
                // When menu opens, set up focus trap
                this.addGlobalFocusTrap();
            } else {
                // When menu closes, remove focus trap if country selector is also closed
                if (!this.showCountries) {
                    this.removeGlobalFocusTrap();
                } else {
                    // Country selector is still open, update focus trap for country selector only
                    this.addGlobalFocusTrap();
                }
            }
        },

        showCountries: function (visible) {
            if (visible) {
                // When country selector opens, set up focus trap (takes priority)
                this.addGlobalFocusTrap();
            } else {
                // When country selector closes, remove focus trap if menu is also closed
                if (!this.showMenu) {
                    this.removeGlobalFocusTrap();
                } else {
                    // Menu is still open, update focus trap for menu only
                    this.addGlobalFocusTrap();
                }
            }
        },
    },
};
